import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, Observable, of, throwError} from 'rxjs';
import {environment} from '../../environment';
import {PeoClientApiModel} from '../model/peo-client-api.model';
import {PeoClientApiManyRequestModel} from '../model/peo-client-api-many-request.model';

/**
 * @class
 * @description This class provides methods to interact with the PEO Clients API.
 * @export
 * @name PeoClientService
 * @service
 */
@Injectable({

  // We'll want this service to be provided as a singleton.
  providedIn: 'root'
})
export class PeoClientService {

  /**
   * @constructor
   * @description This method instantiates a new instance of our PEO service provider with injected dependencies.
   * @memberof PeoClientService
   * @name PeoService#constructor()
   * @param {HttpClient} httpClient The injected instance of our HTTP Client service provider.
   */
  public constructor(
    /**
     * @description This property contains the injected instance of our HTTP Client service provider.
     * @memberof PeoClientService
     * @name PeoClientService#httpClient
     * @private
     * @property
     * @readonly
     * @type {HttpClient}
     */
    private readonly httpClient: HttpClient,
  ) {
  }

  /**
   * @async
   * @description This method asynchronously deletes a PEO Client.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#delete()
   * @param {string} peoId The unique identifier of the PEO to delete the client from.
   * @param {string} clientId The unique identifier of the client to delete.
   */
  public delete(peoId: string, clientId: string): Observable<PeoClientApiModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient
      .delete<PeoClientApiModel>(environment.normalizedApiUrl('/api/administrator/peo/:peoId/client/:clientId', {
        peoId, clientId
      }), {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously lists the PEO Clients from the .NET API.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#list()
   * @param {string} [peoId] The unique identifier of the PEO to list the clients for.
   * @public
   * @returns {Observable<PeoClientApiModel[]>} An observable containing the response from the .NET Backend API.
   */
  public list(peoId?: string): Observable<PeoClientApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<PeoClientApiModel[]>(!!peoId
      ? environment.normalizedApiUrl('/api/administrator/peo/:peoId/client', {peoId})
      : environment.normalizedApiUrl('/api/administrator/peo/client'), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }

  /**
   * @async
   * @description This method asynchronously loads a PEO Client from the .NET API.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#load()
   * @param {string} peoId The unique identifier of the PEO to load the client from.
   * @param {string} clientId The unique identifier of the client to load.
   * @public
   * @returns {Observable<PeoClientApiModel | null>} An observable containing the response from the .NET Backend API.
   */
  public load(peoId: string, clientId: string): Observable<PeoClientApiModel | null> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<PeoClientApiModel>(environment
      .normalizedApiUrl('/api/administrator/peo/:peoId/client/:clientId', {peoId, clientId}), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    }).pipe(catchError((e: HttpErrorResponse): Observable<null> => e.status === 404
      ? of(null)
      : throwError((): HttpErrorResponse => e)));
  }

  /**
   * @async
   * @description This method asynchronously loads the document split status for a PEO Client from the .NET API.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#loadDocumentSplitStatus()
   * @param {string} peoId The unique identifier of the PEO to load the client from.
   * @param {string} clientId The unique identifier of the client to load.
   * @param {number} [taxYear] The tax year to load the document split status for.
   * @public
   * @returns {Observable<PeoClientApiModel>} An observable containing the response from the .NET Backend API.
   */
  public loadDocumentSplitStatus(peoId: string, clientId: string, taxYear?: number): Observable<PeoClientApiModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.get<PeoClientApiModel>(!!taxYear ?
      environment.normalizedApiUrl('/api/administrator/peo/:peoId/client/:clientId/split-document-status/:taxYear',
        {peoId, clientId, taxYear})
      : environment.normalizedApiUrl('/api/administrator/peo/:peoId/client/:clientId/split-document-status',
        {peoId, clientId}), {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }

  /**
   * @async
   * @description This method asynchronously loads multiple PEO Clients from the .NET API.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#loadMany()
   * @param {PeoClientApiManyRequestModel[]} clients The PEO Clients to load.
   * @public
   * @returns {Observable<PeoClientApiModel[]>} An observable containing the response from the .NET Backend API.
   */
  public loadMany(clients: PeoClientApiManyRequestModel[]): Observable<PeoClientApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<PeoClientApiModel[]>(environment.normalizedApiUrl('/api/administrator/peo/client/load'),
      clients, {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously reverts the Customer print settings to default for a list of PEO Clients.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#revertManyPrintSettingsForCustomer()
   * @param {PeoClientApiManyRequestModel[]} clients The PEO Clients to revert the print settings for.
   * @param {number} [taxYear] The tax year to revert the print settings for.
   * @public
   * @returns {Observable<PeoClientApiModel[]>} An observable containing the response from the .NET Backend API.
   */
  public revertManyPrintSettingsForCustomer(clients: PeoClientApiManyRequestModel[], taxYear?: number):
    Observable<PeoClientApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<PeoClientApiModel[]>(!!taxYear
      ? environment.normalizedApiUrl('/api/administrator/peo/client/print-settings/customer/revert/:taxYear', {taxYear})
      : environment.normalizedApiUrl('/api/administrator/peo/client/print-settings/customer/revert'), clients, {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }

  /**
   * @async
   * @description This method asynchronously reverts the Customer print settings to default for a PEO Client.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#revertPrintSettingsForCustomer()
   * @param {string} peoId The unique identifier of the PEO to load the client from.
   * @param {string} clientId The unique identifier of the client to load.
   * @param {number} [taxYear] The tax year to load the print settings for.
   * @public
   * @returns {Observable<any>} An observable containing the response from the .NET Backend API.
   */
  public revertPrintSettingsForCustomer(peoId: string, clientId: string, taxYear?: number): Observable<any> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.patch(!!taxYear ?
      environment.normalizedApiUrl('/api/administrator/peo/:peoId/client/:clientId/print-settings/revert/customer/:taxYear',
        {peoId, clientId, taxYear})
      : environment.normalizedApiUrl('/api/administrator/peo/:peoId/client/:clientId/print-settings/revert/customer',
        {peoId, clientId}), null, {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }

  /**
   * @async
   * @description This method asynchronously saves a PEO Client.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#save()
   * @param {PeoClientApiModel} model The PEO Client to save.
   * @param {boolean} [includePrismHrPrintSettings] Denotes whether to include the PrismHR print settings reconfiguration in the save operation.
   * @public
   * @returns {Observable<PeoClientApiModel>} An observable containing the response from the .NET Backend API.
   */
  public save(model: PeoClientApiModel, includePrismHrPrintSettings: boolean = false): Observable<PeoClientApiModel> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<PeoClientApiModel>(environment
        .normalizedApiUrl('/api/administrator/peo/client/:includePrismHrPrintSettings', {includePrismHrPrintSettings}),
      model, {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously saves multiple PEO Clients.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#save()
   * @param {PeoClientApiModel[]} models The PEO Clients to save.
   * @param {boolean} [includePrismHrPrintSettings] Denotes whether to include the PrismHR print settings reconfiguration in the save operation.
   * @public
   * @returns {Observable<PeoClientApiModel[]>} An observable containing the response from the .NET Backend API.
   */
  public saveMany(models: PeoClientApiModel[], includePrismHrPrintSettings: boolean = false): Observable<PeoClientApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<PeoClientApiModel[]>(environment
        .normalizedApiUrl('/api/administrator/peo/client/save/:includePrismHrPrintSettings', {includePrismHrPrintSettings}),
      models, {

        // Set the with credentials flag into the request options.
        withCredentials: true
      });
  }

  /**
   * @async
   * @description This method asynchronously saves the Customer print settings to the PrismHR configuration for a list of PEO Clients.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#saveManyPrintSettingsForCustomer()
   * @param {PeoClientApiManyRequestModel[]} clients The PEO Clients to save the print settings for.
   * @param {number} [taxYear] The tax year to save the print settings for.
   * @public
   * @returns {Observable<PeoClientApiModel[]>} An observable containing the response from the .NET Backend API.
   */
  public saveManyPrintSettingsForCustomer(clients: PeoClientApiManyRequestModel[], taxYear?: number):
    Observable<PeoClientApiModel[]> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.post<PeoClientApiModel[]>(!!taxYear
      ? environment.normalizedApiUrl('/api/administrator/peo/client/print-settings/customer/save/:taxYear', {taxYear})
      : environment.normalizedApiUrl('/api/administrator/peo/client/print-settings/customer/save'), clients, {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }

  /**
   * @async
   * @description This method asynchronously saves the Customer print settings for a PEO Client.
   * @memberof PeoClientService
   * @method
   * @name PeoClientService#savePrintSettingsForCustomer()
   * @param {string} peoId The unique identifier of the PEO to load the client from.
   * @param {string} clientId The unique identifier of the client to load.
   * @param {number} [taxYear] The tax year to load the print settings for.
   * @public
   * @returns {Observable<any>} An observable containing the response from the .NET Backend API.
   */
  public savePrintSettingsForCustomer(peoId: string, clientId: string, taxYear?: number): Observable<any> {

    // Return the observable from the HTTP Client service provider.
    return this.httpClient.patch(!!taxYear ?
      environment.normalizedApiUrl('/api/administrator/peo/:peoId/client/:clientId/print-settings/customer/:taxYear',
        {peoId, clientId, taxYear})
      : environment.normalizedApiUrl('/api/administrator/peo/:peoId/client/:clientId/print-settings/customer',
        {peoId, clientId}), null, {

      // Set the with credentials flag into the request options.
      withCredentials: true
    });
  }
}
